import { useImmerAtom } from 'jotai-immer';
import { feedsSearchFormAtom } from './feeds_search_atoms';
import { OpsFeedsSearchModel } from './feeds_search_types';
import { useCallback, useMemo } from 'react';
import { ngDelegate } from '../../../../core/services/ng.delegate';
import { cleanFeedsSearchPayload } from './cleanFeedsSearchPayload';
import {
    getCustom3DateRangeError,
    getCustom92DateRangeError,
} from '../../../../core/components/validation/ValidInputDate.utils';
import { useFeedsStoreActions } from '../pagehooks/useFeedsStore';
import tableViewRepo from '../../../../core/repo/viewStateRepo';
import { FEEDS_LIST_FORM_KEY, feedsUserContextAtom } from '../feeds_list_atoms';
import { stripAwayFormErrors } from '../../../../core/table/utils/stripAwayFormErrors';
import { getUiModeForFeeds } from '../pagehooks/useFeedsInit';
import { useAtomValue } from 'jotai';

export const validateFeedType = (prop: OpsFeedsSearchModel['FeedType']): boolean => {
    if (Array.isArray(prop?.val) && prop.val.length === 0) {
        return false;
    } else {
        return true;
    }
};

export const validateCE = (prop: OpsFeedsSearchModel['CE']): boolean => {
    if (Array.isArray(prop?.val) && prop.val.length === 0) {
        return false;
    } else {
        return true;
    }
};

export const validateRX = (prop: OpsFeedsSearchModel['RX']): boolean => {
    if (Array.isArray(prop?.val) && prop.val.length === 0) {
        return false;
    } else {
        return true;
    }
};

export const validateSource = (prop: OpsFeedsSearchModel['Source']): boolean => {
    if (Array.isArray(prop?.val) && prop.val.length === 0) {
        return false;
    } else {
        return true;
    }
};

export const validateStatus = (prop: OpsFeedsSearchModel['Status']): boolean => {
    if (Array.isArray(prop?.val) && prop.val.length === 0) {
        return false;
    } else {
        return true;
    }
};

type UseFeedsSearchStateReturn = {
    state: OpsFeedsSearchModel;
    setProperty: (key: keyof OpsFeedsSearchModel, val: any) => void;
    setMultipleProperties: (updates: Partial<Record<keyof OpsFeedsSearchModel, any>>) => void;
    toggleChecked: (key: keyof OpsFeedsSearchModel, val: boolean) => void;
    handleSearch: () => void;
};

export const useFeedsSearchState = (): UseFeedsSearchStateReturn => {
    const { isImpersonating } = useAtomValue(feedsUserContextAtom);
    const [state, _setState] = useImmerAtom(feedsSearchFormAtom);
    const { getList } = useFeedsStoreActions();
    const uiMode = useMemo(() => getUiModeForFeeds(), []);

    const setProperty = useCallback(
        (key: keyof OpsFeedsSearchModel, val: any): void => {
            // Runtime type check for multi-select fields
            const multiSelectKeys = ['CE', 'RX', 'FeedType', 'Status', 'Source'];
            if (multiSelectKeys.includes(key as string) && !Array.isArray(val)) {
                // eslint-disable-next-line no-console
                console.warn(
                    `[FeedsSearchState] setProperty: Attempted to set non-array value for multi-select field '${key}'. Value:`,
                    val,
                );
                // Optionally, throw or coerce to array
            }
            _setState(draft => {
                draft[key].val = val;
                if (multiSelectKeys.includes(key as string)) {
                    if (Array.isArray(val) && val.length === 0) {
                        const labels: Record<string, string> = {
                            CE: 'Facility',
                            RX: 'Pharmacy',
                            FeedType: 'Feed Type',
                            Source: 'Source',
                            Status: 'Status',
                        };
                        draft[key].err = `At least one ${labels[key as string]} is required.`;
                    } else {
                        draft[key].err = '';
                    }
                }
            });
        },
        [_setState],
    );

    const setMultipleProperties = useCallback(
        (updates: Partial<Record<keyof OpsFeedsSearchModel, any>>): void => {
            _setState(draft => {
                for (const [key, val] of Object.entries(updates)) {
                    (draft as any)[key].val = val;
                }
            });
        },
        [_setState],
    );

    const toggleChecked = useCallback(
        (key: keyof OpsFeedsSearchModel, val: boolean): void => {
            _setState(draft => {
                (draft[key] as any).checked = val;
            });
        },
        [_setState],
    );

    // Primitive keys derived from the selected values — used as stable dep signals below.
    // Intentionally omit the full state.FeedType / state.CE objects so that writing back
    // .err (which happens inside validate itself) does not invalidate the callback.
    const feedTypeValKey = state.FeedType?.val?.join(',');
    const ceValKey = state.CE?.val?.join(',');
    const rxValKey = state.RX?.val?.join(',');
    const sourceValKey = state.Source?.val?.join(',');
    const statusValKey = state.Status?.val?.join(',');

    const validate = useCallback((): boolean => {
        // Extra safety: block submission if custom date range is invalid
        if (state.DateRangeMode?.val === 'CUSTOM3' && state.StartDate?.val && state.EndDate?.val) {
            const err = getCustom3DateRangeError(state.StartDate.val, state.EndDate.val);
            if (err) return false;
        }
        if (state.DateRangeMode?.val === 'CUSTOM92' && state.StartDate?.val && state.EndDate?.val) {
            const err = getCustom92DateRangeError(state.StartDate.val, state.EndDate.val);
            if (err) return false;
        }

        const feedType = validateFeedType(state.FeedType);
        const ce = validateCE(state.CE);
        const rx = validateRX(state.RX);
        const source = validateSource(state.Source);
        const status = validateStatus(state.Status);

        _setState(draft => {
            if (!feedType) {
                draft.FeedType.err = 'At least one Feed Type is required.';
            } else {
                draft.FeedType.err = '';
            }
            if (!ce) {
                draft.CE.err = 'At least one Facility is required.';
            } else {
                draft.CE.err = '';
            }
            if (!rx) {
                draft.RX.err = 'At least one Pharmacy is required.';
            } else {
                draft.RX.err = '';
            }
            if (!source) {
                draft.Source.err = 'At least one Source is required.';
            } else {
                draft.Source.err = '';
            }
            if (!status) {
                draft.Status.err = 'At least one Status is required.';
            } else {
                draft.Status.err = '';
            }
        });

        const hasErrors = !feedType || !ce || !rx || !source || !status;
        return !hasErrors;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        state.DateRangeMode?.val,
        state.StartDate?.val,
        state.EndDate?.val,
        feedTypeValKey,
        ceValKey,
        rxValKey,
        sourceValKey,
        statusValKey,
        _setState,
    ]);

    const handleSearch = useCallback(async () => {
        const cleanedState = cleanFeedsSearchPayload(state, uiMode);
        // Save form viewstate to localStorage (match Provider behavior)
        if (!isImpersonating) {
            tableViewRepo.setForm(FEEDS_LIST_FORM_KEY, stripAwayFormErrors(state));
        }
        // Delegate to store's getList, which handles API, state, and data atom updates
        getList(cleanedState);
    }, [getList, state, uiMode, isImpersonating]);

    const submit = useCallback((): void => {
        if (!validate()) {
            ngDelegate.ok('Cannot Search', 'Check properties');
            return;
        } else {
            handleSearch();
        }
    }, [validate, handleSearch]);

    return {
        state,
        setProperty,
        setMultipleProperties,
        toggleChecked,
        handleSearch: submit,
    };
};
